/**
 * Unified Provider SSO Handler
 * Handles SSO for all providers with proper separation of concerns
 */

import { launchBookrSso, isBookrProvider, getBookrErrorMessage } from './bookrSso.js';
import { launchBookrNextSso, isBookrNextProvider, getBookrNextErrorMessage } from './bookrNextSso.js';
import { isHighlightsLibraryProvider, getHighlightsLibraryErrorMessage } from './highlightsSso.js';
import { launchOpenElmsSso, isOpenElmsProvider, getOpenElmsErrorMessage } from './openElmsSso.js';
import { launchBoclipsSso, isBoclipsProvider, getBoclipsErrorMessage } from './boclipsSso.js';
import { launchScholarlabSso, isScholarlabProvider, getScholarlabErrorMessage } from './scholarlabSso.js';

/**
 * Handle provider click - either launch SSO or open website
 * @param {Object} provider - Provider object
 * @param {string} userId - Current user ID
 * @param {Function} onError - Error callback function
 * @param {Function} [navigate] - Navigation function (from react-router-dom)
 */
export async function handleProviderClick(provider, userId, onError, navigate) {
  if (!provider) {
    onError?.('Provider not found');
    return;
  }

  const isBookr = isBookrProvider(provider);
  const isBookrNext = isBookrNextProvider(provider);
  const isHighlightsLibrary = isHighlightsLibraryProvider(provider);
  const isOpenElms = isOpenElmsProvider(provider);
  const isBoclips = isBoclipsProvider(provider);
  const isScholarlab = isScholarlabProvider(provider);

  if (isBookr) {
    const result = await launchBookrSso(userId, {
      ttlSeconds: 300,
      returnUrl: window.location.origin,
    });
    if (!result.success) {
      onError?.(getBookrErrorMessage(result.error));
    }
  } else if (isBookrNext) {
    const result = await launchBookrNextSso(userId, {
      ttlSeconds: 300,
      returnUrl: window.location.origin,
    });
    if (!result.success) {
      onError?.(getBookrNextErrorMessage(result.error));
    }
  } else if (isHighlightsLibrary) {
    // Use the same direct sign-in destination for every account until SSO is ready.
    window.open(
      'https://library.highlights.com/member/login',
      '_blank',
      'noopener,noreferrer',
    );
  } else if (isOpenElms) {
    const result = await launchOpenElmsSso(userId, {
      ttlSeconds: 300,
      returnUrl: window.location.origin,
    });
    if (!result.success) {
      onError?.(getOpenElmsErrorMessage(result.error));
    }
  } else if (isBoclips) {
    const result = await launchBoclipsSso(userId, {
      navigate,
      openInNewTab: true,
    });
    if (!result.success) {
      onError?.(getBoclipsErrorMessage(result.error));
    }
  } else if (isScholarlab) {
    const result = await launchScholarlabSso(userId, {
      navigate,
      openInNewTab: true,
    });
    if (!result.success) {
      onError?.(getScholarlabErrorMessage(result.error));
    }
  } else if (isEverybodyCountsProvider(provider)) {
    // Use the same direct sign-in destination for every account until SSO is ready.
    window.open(
      'https://europe.everybodycounts.tech/login',
      '_blank',
      'noopener,noreferrer',
    );
  } else if (provider.website) {
    window.open(provider.website, '_blank', 'noopener,noreferrer');
  } else {
    onError?.('No website available for this provider');
  }
}

/**
 * Everybody Counts uses our OIDC IdP; launch is via /api/oidc/launch/everybodycounts
 */
export function isEverybodyCountsProvider(providerOrKey) {
  const key = typeof providerOrKey === 'string' ? providerOrKey : providerOrKey?.key;
  const name = typeof providerOrKey === 'object' && providerOrKey?.name;
  if (key) {
    const k = String(key).toLowerCase().replace(/\s+/g, '');
    if (k === 'everybodycounts' || k === 'everybody-counts') return true;
  }
  if (name && /everybody\s*counts/i.test(name)) return true;
  return false;
}

/**
 * Get user-friendly error messages for any provider SSO errors
 * @param {string} error - Error message
 * @param {string} providerType - Type of provider ('bookr', 'bookr-class', 'bookr-next', etc.)
 * @returns {string} User-friendly error message
 */
export function getProviderErrorMessage(error, providerType = '') {
  if (providerType === 'bookr' || providerType === 'bookr-class') {
    return getBookrErrorMessage(error);
  }

  if (providerType === 'bookr-next') {
    return getBookrNextErrorMessage(error);
  }

  if (providerType === 'highlights-library' || providerType === 'highlights') {
    return getHighlightsLibraryErrorMessage(error);
  }

  if (providerType === 'open-elms' || providerType === 'openelms') {
    return getOpenElmsErrorMessage(error);
  }

  if (providerType === 'boclips') {
    return getBoclipsErrorMessage(error);
  }

  if (providerType === 'scholarlab' || providerType === 'scholar-lab') {
    return getScholarlabErrorMessage(error);
  }

  if (providerType === 'everybodycounts' || providerType === 'everybody-counts') {
    return 'Failed to launch Everybody Counts. Please try again or contact support.';
  }

  // Generic error handling for unknown providers
  const errorLower = (error || '').toLowerCase();

  if (errorLower.includes('unauthorized') || errorLower.includes('api key')) {
    return 'Authentication failed. Please contact support.';
  }

  if (errorLower.includes('user_not_found')) {
    return 'User account not found. Please contact support.';
  }

  if (errorLower.includes('popup')) {
    return 'Please allow popups for this site and try again.';
  }

  if (errorLower.includes('network') || errorLower.includes('fetch')) {
    return 'Network error. Please check your connection and try again.';
  }

  if (errorLower.includes('configuration error') || errorLower.includes('react_app_api_base_url')) {
    return 'System configuration error. Please contact support.';
  }

  return error || 'An unexpected error occurred. Please try again.';
}
